import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { AmountInput } from "../components/ui/amount-input";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from "../components/ui/sheet";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Banknote, FileDown, FileText, Pencil, Archive as ArchiveIcon } from "lucide-react";
import { formatINR, formatDate, formatDateTime, fileToBase64, todayISO, displayAcCode } from "../lib/format";
import { openAuthDownload } from "../lib/download";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { PaymentActions } from "../components/PaymentActions";
import { EntryDetailDialog } from "../components/EntryDetailDialog";
import { BackdateFields, backdatePayload } from "../components/BackdateFields";
import { useDuplicateGuard, todayIST } from "../components/DuplicateGuard";

export default function PigmyDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const { guard, dialog: dupDialog } = useDuplicateGuard();
    const isAdmin = user?.role === "admin";
    const [acct, setAcct] = useState(null);
    const [entries, setEntries] = useState([]);
    const [detailId, setDetailId] = useState(null);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({ amount: 0, mode: "cash", notes: "", proof: "" });
    const [backdate, setBackdate] = useState({});
    const [editOpen, setEditOpen] = useState(false);
    const [editForm, setEditForm] = useState({
        customer_id: "",
        frequency: "daily",
        installment_amount: 100,
        tenure_months: 12,
        start_date: todayISO(),
        notes: "",
    });

    async function load() {
        const [a, b] = await Promise.all([
            api.get(`/pigmy/accounts/${id}`),
            api.get(`/pigmy/accounts/${id}/entries`),
        ]);
        setAcct(a.data);
        setEntries(b.data);
    }
    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [id]);

    async function submit(e) {
        e.preventDefault();
        if (!form.amount || form.amount <= 0) return toast.error("Enter amount");
        await guard({
            collection_type: "pigmy", pigmy_account_id: id, customer_id: acct.customer_id, amount: Number(form.amount),
            on_date: backdate.enabled && backdate.collection_datetime ? backdate.collection_datetime.slice(0, 10) : todayIST(),
        }, doSubmit);
    }

    async function doSubmit() {
        setBusy(true);
        try {
            await api.post("/collections", {
                pigmy_account_id: id,
                customer_id: acct.customer_id,
                collection_type: "pigmy",
                amount: Number(form.amount),
                mode: form.mode,
                notes: form.notes,
                proof: form.proof,
                ...backdatePayload(backdate),
            });
            setBackdate({});
            toast.success("Submitted for admin approval");
            setOpen(false);
            setForm({ amount: 0, mode: "cash", notes: "", proof: "" });
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }

    async function closeAccount() {
        try {
            await api.post(`/pigmy/accounts/${id}/close`);
            toast.success("Account marked matured");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function saveEdit() {
        try {
            await api.put(`/pigmy/accounts/${id}`, editForm);
            toast.success("Saving Scheme account updated");
            setEditOpen(false);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function archiveAcct() {
        const reason = prompt("Reason for archiving (optional):") ?? "";
        try {
            await api.post(`/pigmy/accounts/${id}/archive`, { reason });
            toast.success("Saving Scheme account archived");
            window.location.href = "/pigmy";
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    function openEdit() {
        if (!acct) return;
        setEditForm({
            customer_id: acct.customer_id,
            frequency: acct.frequency,
            installment_amount: acct.installment_amount,
            tenure_months: acct.tenure_months,
            start_date: acct.start_date,
            notes: acct.notes || "",
        });
        setEditOpen(true);
    }

    if (!acct) return <div className="text-slate-500">Loading…</div>;
    const c = acct.customer || {};

    return (
        <div className="space-y-5">
            <Card className="rounded-2xl border-slate-100 p-5 sm:p-6">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h1 className="font-display text-2xl font-bold text-slate-900">
                                {c.name}
                            </h1>
                            <span className="text-[10px] font-mono uppercase bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                {displayAcCode(acct.account_code)}
                            </span>
                        </div>
                        <div className="text-sm text-slate-500 mt-1 capitalize">
                            {acct.frequency} · Installment {formatINR(acct.installment_amount)}
                        </div>
                    </div>
                    <Badge className={acct.status === "active"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-50"
                        : "bg-slate-100 text-slate-700"}>
                        {acct.status}
                    </Badge>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 pt-5 border-t border-slate-100">
                    <Info label="Balance" value={formatINR(acct.balance)} accent />
                    <Info label="Total Deposited" value={formatINR(acct.total_deposited)} />
                    <Info label="Start Date" value={formatDate(acct.start_date)} />
                    <Info label="Maturity" value={formatDate(acct.maturity_date)} />
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <Button data-testid="pigmy-collect-btn" disabled={acct.status !== "active"}
                                className="h-11 rounded-xl bg-blue-900 hover:bg-blue-800 tap">
                                <Banknote className="h-4 w-4 mr-1.5" /> Add Deposit
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="bottom" className="rounded-t-2xl">
                            <SheetHeader>
                                <SheetTitle>Saving Scheme Deposit</SheetTitle>
                            </SheetHeader>
                            <form onSubmit={submit} className="space-y-3 mt-3">
                                <div>
                                    <Label>Amount (₹)</Label>
                                    <AmountInput data-testid="pigmy-deposit-amount" required value={form.amount}
                                        onChange={(v) => setForm((s) => ({ ...s, amount: v }))}
                                        className="h-11 rounded-xl" />
                                </div>
                                <div>
                                    <Label>Mode</Label>
                                    <Select value={form.mode} onValueChange={(v) => setForm((s) => ({ ...s, mode: v }))}>
                                        <SelectTrigger className="h-11 rounded-xl"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="cash">Cash</SelectItem>
                                            <SelectItem value="upi">UPI</SelectItem>
                                            <SelectItem value="qr">Bank QR</SelectItem>
                                            <SelectItem value="bank">Bank Transfer</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label>Notes</Label>
                                    <Textarea rows={2} value={form.notes}
                                        onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))}
                                        className="rounded-xl" />
                                </div>
                                <div>
                                    <Label>Payment Proof</Label>
                                    <input type="file" accept="image/*" onChange={async (e) => {
                                        const f = e.target.files?.[0];
                                        if (!f) return;
                                        const b64 = await fileToBase64(f);
                                        setForm((s) => ({ ...s, proof: b64 }));
                                    }} className="block text-sm" />
                                </div>
                                <BackdateFields value={backdate} onChange={setBackdate} />
                                <Button type="submit" disabled={busy} data-testid="pigmy-deposit-submit"
                                    className="w-full h-12 rounded-xl bg-blue-900 hover:bg-blue-800">
                                    {busy ? "Submitting…" : "Submit (Pending Approval)"}
                                </Button>
                            </form>
                        </SheetContent>
                    </Sheet>

                    {isAdmin && (
                        <Button variant="outline" className="h-11 rounded-xl tap font-semibold"
                            data-testid="pigmy-edit-btn" onClick={openEdit}>
                            <Pencil className="h-4 w-4 mr-1.5" /> Edit
                        </Button>
                    )}
                    {isAdmin && !acct.archived && (
                        <Button variant="outline"
                            className="h-11 rounded-xl text-amber-700 border-amber-200 hover:bg-amber-50 tap"
                            data-testid="pigmy-archive-btn" onClick={archiveAcct}>
                            <ArchiveIcon className="h-4 w-4 mr-1.5" /> Archive
                        </Button>
                    )}
                    {user?.role === "admin" && (
                        <Button variant="outline" className="h-11 rounded-xl" data-testid="pigmy-opening-cert"
                            onClick={() => openAuthDownload(`/certificates/pigmy-opening/${id}`)}>
                            <FileText className="h-4 w-4 mr-1.5" /> Opening Certificate
                        </Button>
                    )}
                    <Button variant="outline" className="h-11 rounded-xl" data-testid="pigmy-passbook"
                        onClick={() => openAuthDownload(`/passbooks/pigmy/${id}`)}>
                        <FileDown className="h-4 w-4 mr-1.5" /> Download Passbook
                    </Button>
                    {user?.role === "admin" && acct.status === "active" && (
                        <Button variant="outline" className="h-11 rounded-xl" onClick={closeAccount}
                            data-testid="pigmy-close-btn">
                            Mark Matured
                        </Button>
                    )}
                    {acct.status !== "active" && (
                        <Button variant="outline" className="h-11 rounded-xl" data-testid="pigmy-maturity-cert"
                            onClick={() => openAuthDownload(`/certificates/pigmy-maturity/${id}`)}>
                            Maturity Certificate
                        </Button>
                    )}
                </div>
            </Card>

            <Card className="rounded-2xl border-slate-100">
                <div className="p-5 pb-2 font-display text-lg font-bold text-slate-900">Deposits</div>
                {entries.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-400">No deposits yet</div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {entries.slice().reverse().map((p) => (
                            <div key={p.id} className="p-4 flex items-start justify-between gap-3">
                                <div>
                                    <div className="text-xs text-slate-500 cursor-pointer hover:text-blue-900 underline-offset-2 hover:underline"
                                        role="button" tabIndex={0}
                                        data-testid={`pigmy-entry-open-${p.id}`}
                                        onClick={() => setDetailId(p.id)}
                                        onKeyDown={(e) => { if (e.key === "Enter") setDetailId(p.id); }}>
                                        {formatDateTime(p.collection_datetime || p.payment_date)} · <span className="uppercase">{p.mode}</span> · {p.staff}
                                    </div>
                                    {p.notes && <div className="text-sm text-slate-700 mt-0.5">{p.notes}</div>}
                                    {isAdmin && <PaymentActions payment={p} kind="pigmy" onDone={load} />}
                                </div>
                                <div className="text-right shrink-0">
                                    <div className={`num font-bold ${Number(p.total) < 0 ? "text-rose-700" : "text-slate-900"}`}>
                                        {Number(p.total) < 0
                                            ? `- ${formatINR(Math.abs(Number(p.total)))}`
                                            : formatINR(p.total)}
                                    </div>
                                    <div className="text-[11px] text-slate-500">
                                        Bal: {formatINR(p.balance_after)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="rounded-2xl">
                    <DialogHeader>
                        <DialogTitle>Edit Saving Scheme Account</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Frequency</Label>
                                <Select value={editForm.frequency} onValueChange={(v) => setEditForm((s) => ({ ...s, frequency: v }))}>
                                    <SelectTrigger className="h-11 rounded-xl" data-testid="edit-pigmy-frequency"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="daily">Daily</SelectItem>
                                        <SelectItem value="weekly">Weekly</SelectItem>
                                        <SelectItem value="monthly">Monthly</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Installment (₹)</Label>
                                <AmountInput data-testid="edit-pigmy-installment" value={editForm.installment_amount}
                                    onChange={(v) => setEditForm((s) => ({ ...s, installment_amount: v }))}
                                    className="h-11 rounded-xl" />
                            </div>
                            <div>
                                <Label>Tenure (months)</Label>
                                <AmountInput data-testid="edit-pigmy-tenure" value={editForm.tenure_months}
                                    onChange={(v) => setEditForm((s) => ({ ...s, tenure_months: v }))}
                                    className="h-11 rounded-xl" />
                            </div>
                            <div>
                                <Label>Start Date</Label>
                                <Input data-testid="edit-pigmy-start" type="date" value={editForm.start_date}
                                    onChange={(e) => setEditForm((s) => ({ ...s, start_date: e.target.value }))}
                                    className="h-11 rounded-xl" />
                            </div>
                        </div>
                        <div>
                            <Label>Notes</Label>
                            <Textarea rows={2} value={editForm.notes}
                                onChange={(e) => setEditForm((s) => ({ ...s, notes: e.target.value }))}
                                className="rounded-xl" data-testid="edit-pigmy-notes" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
                        <Button onClick={saveEdit} data-testid="save-pigmy-edit" className="bg-blue-900 hover:bg-blue-800">
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <EntryDetailDialog paymentId={detailId} open={!!detailId} onClose={() => setDetailId(null)} />
            {dupDialog}
        </div>
    );
}

function Info({ label, value, accent }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
            <div className={`num text-base font-bold mt-0.5 ${accent ? "text-blue-900 text-lg" : "text-slate-900"}`}>
                {value}
            </div>
        </div>
    );
}
